import React from 'react';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';
import { bindActionCreators } from 'redux';
import selectOptions, { getClassificationPrefName } from '../../../text/selectOptions';
import { FormattedMessage } from 'react-intl';
import messages from '../../../text/messages';
import WizardButtons from '../../../components/wizard/navigator/buttons';
import { getFormTypeQuestions } from '../../../reducers/formInstances';
import { sendJobInvite, getPosition, approvePosition } from '../../../reducers/positions';
import { history } from '../../../configuration/store';
import Table from '../../../components/table';
import { getAddress, getCurrencySymbol, getFullName, ifExists } from '../../../utilities/string';
import { dateToFloatingString } from '../../../utilities/dateTime';
import { Snackbar } from '../../../components/snackbar';

import './steps.css';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

class PositionTheReviewStep extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isModalOpen: false
        };

        this.getPO = this.getPO.bind(this);
        this.getPayment = this.getPaymentValue.bind(this);
        this.getProjectsValue = this.getProjectsValue.bind(this);
        this.getLocation = this.getLocationValue.bind(this);
        this.submit = this.submit.bind(this);
        this.getClientQuestions = this.getClientQuestions.bind(this);
        this.getTableData = this.getTableData.bind(this);
        this.getJobPeriod = this.getJobPeriod.bind(this);
    }

    showError(error) {
        Snackbar.error((error || '').includes('already exists') ? <FormattedMessage {...messages.contractorExists} /> : error);
    }

    submit() {
        const { position, sendJobInvite, approvePosition } = this.props;
        approvePosition(position.id);
        sendJobInvite(
            { ...position.progress.worker, projects: position.progress.projects, rate_currency: position.progress.projects[0].rate_currency },
            () => history.push('/'),
            this.showError
        );
    }

    getClientQuestions(questions) {
        const { initialValues, userDetails, getFormTypeQuestions } = this.props;
        const positionId = initialValues.id;

        const formType = 'job_classification_client';

        if (positionId && !questions) {
            getFormTypeQuestions(formType, userDetails.admin_id, positionId);
        }
    }

    getLocationValue() {
        const { work_location_type, work_location_address } = this.props.position;

        return work_location_type === 'offsite' ? (
            <div>
                <FormattedMessage {...messages.workLocationRemote} />
            </div>
        ) : (
            <div>{getAddress(work_location_address)}</div>
        );
    }

    getPaymentValue(project) {
        const { projects, position } = this.props;
        const { pay_type } = position;
        const { project_value, rate_currency, rate, project_id, hours_per_week } = project;
        const projectName = projects.find(p => p.id === project_id).name;

        return pay_type === 's' ? (
            <div>
                <span>{projectName}</span>
                {': '}
                <FormattedMessage {...messages.totalValue} />
                {': '}
                <span>
                    {getCurrencySymbol(rate_currency)} {project_value}
                </span>
            </div>
        ) : (
            <div>
                <span>{projectName}</span>
                {': '}
                {getCurrencySymbol(rate_currency)}
                {rate && parseFloat(rate).toFixed(2)}
                {'/hour'} {hours_per_week && `(${hours_per_week} hours/week)`}
            </div>
        );
    }

    getProjectsValue() {
        const { projects = [] } = this.props.position.progress;

        return projects.map(p => this.getPaymentValue(p));
    }

    getJobPeriod(start_date, end_date) {
        const label = <FormattedMessage {...messages.period} />;
        const startDateValue = dateToFloatingString(start_date);
        const endDateValue = end_date ? dateToFloatingString(end_date) : '';
        const value = `${startDateValue} - ${endDateValue}`;
        return { label, value };
    }

    getPO(po_when, po_number, po_value, rate_currency) {
        if (po_when === 'never') return;

        const label = <FormattedMessage {...messages.purchaseOrder} />;
        const value =
            po_when !== 'now' ? (
                <div>{selectOptions.purchaseOrderStatus.find(o => o.value === po_when).label}</div>
            ) : (
                <div>
                    <FormattedMessage {...messages.poNumber} /> {` ${po_number}, `}
                    <br />
                    <FormattedMessage {...messages.poValue} /> {`${getCurrencySymbol(rate_currency)}${po_value}`}
                </div>
            );
        return { label, value };
    }

    getDepartment(department) {
        if (!department) return;
        const label = <FormattedMessage {...messages.department} />;
        const value = department;
        return { label, value };
    }

    getDBA(dbaName, country) {
        if (!dbaName) return;
        const label = <FormattedMessage {...(country === 'UK' ? messages.lcName : messages.dbaName)} />;
        const value = dbaName;
        return { label, value };
    }

    getTableData() {
        const { admin, position } = this.props;
        if (!position) return;

        const {
            progress,
            title,
            department,
            hiring_manager,
            classify_client_pref,
            classify_client_result,
            po_when,
            po_number,
            po_value,
            start_date,
            end_date,
            country
        } = position;
        if (!progress) return;

        const { email, phoneNumber, dbaName } = progress.worker || {};

        const tableData = {
            columns: [
                { valueMap: d => <span className="row-label">{d.label}</span>, size: 'col-xs-12 col-md-3' },
                { valueMap: d => <span className="row-value">{d.value}</span>, size: 'col-xs-12 col-md-9' }
            ],
            data: [
                // positionTheWork fields
                { label: <FormattedMessage {...messages.title} />, value: title },
                ...ifExists(this.getDepartment(department)),
                ...ifExists(this.getJobPeriod(start_date, end_date)),
                {
                    label: <FormattedMessage {...messages.reportingTo} />,
                    value: (hiring_manager && getFullName(hiring_manager)) || <FormattedMessage {...messages.notProvided} />
                },
                { label: <FormattedMessage {...messages.country} />, value: country },
                { label: <FormattedMessage {...messages.workLocation} />, value: this.getLocationValue() },

                // positionThePay fields
                { label: <FormattedMessage {...messages.projects} />, value: this.getProjectsValue() },
                ...ifExists(this.getPO(po_when, po_number, po_value, progress.projects[0].rate_currency)),

                // positionTheWorker fields
                { label: <FormattedMessage {...messages.worker} />, value: getFullName(progress.worker) },
                ...ifExists(this.getDBA(dbaName, country)),

                { label: <FormattedMessage {...messages.email} />, value: email },
                { label: <FormattedMessage {...messages.phone} />, value: formatPhoneNumberIntl(phoneNumber) },
                {
                    label: <FormattedMessage {...messages.classifyClientPreference} />,
                    value: getClassificationPrefName(admin, classify_client_pref)
                }
            ]
        };

        const workerClassificationResults = {
            'w2-or-ic': `Evaluated based on worker's information`,
            'w2-only': 'W-2 employee'
        };
        tableData.data.push({
            label: <FormattedMessage {...messages.classifyResult} />,
            value: workerClassificationResults[classify_client_result]
        });

        return tableData;
    }

    componentDidMount() {
        const { questions } = this.props;
        this.getClientQuestions(questions);
    }

    componentDidUpdate() {
        const { questions } = this.props;
        this.getClientQuestions(questions);
    }

    render() {
        const { handleSubmit, back, cancel, submitting } = this.props;
        let tableData = this.getTableData();

        return (
            <div>
                <form onSubmit={handleSubmit(() => this.submit())}>
                    <Table {...tableData} rowStyle={'onboarding-table-row'} />
                    <WizardButtons
                        disabled={submitting}
                        back={{ action: back }}
                        cancel={{ action: cancel }}
                        submit={{ message: <FormattedMessage id={'inviteWorker'} defaultMessage={'Invite worker'} /> }}
                    />
                </form>
            </div>
        );
    }
}

PositionTheReviewStep = reduxForm({
    form: 'position-wizard',
    destroyOnUnmount: false,
    enableReinitialize: true,
    forceUnregisterOnUnmount: true
})(PositionTheReviewStep);

const mapStateToProps = (state, props) => {
    let initialValues = {
        ...props.initialValues,
        ...props.position
    };

    return {
        admin: state.users.adminDetails,
        userDetails: state.identity.details,
        questions: state.formInstances.questions,
        projects: state.projects.list,
        initialValues
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getPosition,
            sendJobInvite,
            approvePosition,
            getFormTypeQuestions
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(PositionTheReviewStep);



// WEBPACK FOOTER //
// ./src/pages/position/steps/positionTheReview.jsx